import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { socketManager } from "../socket/socketManager";

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = React.memo(({ children, user }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null); // { fromUserId, signal, name, discussionId }
  const [inCall, setInCall] = useState(false);
  const [callInfoData, setCallInfoData] = useState(null);
  const userRef = useRef(user);
  const cleanup = useRef(null);

  // Update user ref when user changes
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const handleConnection = useCallback((userId = user?._id) => {
    if (!userId) {
      socketManager.disconnect();
      setIsConnected(false);
      return;
    }

    try {
      const socket = socketManager.connect({
        query: { userId: userId },
      });

      if (socket) {
        // Handler for incoming calls - must be re-attached on reconnect
        const handleCallIncoming = (payload) => {
          console.log(payload, "payload - callIncoming received")
          setIncomingCall(payload);
        };

        // Connection event handlers
        const onConnect = () => {
          setIsConnected(true);
          setConnectionError(null);
          // Re-register user and re-attach callIncoming listener on every connect/reconnect
          if (userId) {
            socket.emit('register', userId);
          }
          // Re-attach callIncoming listener on reconnect
          socket.off('callIncoming', handleCallIncoming); // Remove any existing listener first
          socket.on('callIncoming', handleCallIncoming);
          console.log('Re-attached callIncoming listener on connect/reconnect');
        };

        // Attach callIncoming listener initially
        socket.on('callIncoming', handleCallIncoming);

        // Register user initially
        if (userId && socket.connected) {
          socket.emit('register', userId);
        }

        const onDisconnect = (reason) => {
          setIsConnected(false);
          if (reason !== "io client disconnect") {
            setConnectionError(`Disconnected: ${reason}`);
          }
        };

        const onConnectError = (error) => {
          setConnectionError(error.message || "Connection failed");
          setIsConnected(false);
        };

        // Attach handlers
        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onConnectError);

        // Store cleanup function
        cleanup.current = () => {
          socket.off("connect", onConnect);
          socket.off("disconnect", onDisconnect);
          socket.off("connect_error", onConnectError);
          socket.off('callIncoming', handleCallIncoming);
        };

        // Set initial connection state
        setIsConnected(socket.connected);
      }
    } catch (error) {
      console.error("Socket connection error:", error);
      setConnectionError(error.message);
    }
  }, [user?._id]);

  // Handle visibility change for reconnection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        userRef.current?._id &&
        !socketManager.isConnected()
      ) {
        handleConnection();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [handleConnection]);

  // Main connection effect
  useEffect(() => {
    handleConnection(); // on refreshing the page when user logged in 
    window.addEventListener('registerSocket', (e) => {
      console.log("🚀 ~ window.addEventListener ~ registerSocket:")
      handleConnection(e.detail);// when logged in user is navigate to chat
    })

    window.addEventListener('online', () => {
      if (user?._id && socketManager.isConnected()) {
        socketManager.emit('register', user?._id);
      } else if (user?._id) {
        // If socket is not connected, trigger reconnection
        handleConnection();
      }
    })

    return () => {
      cleanup.current?.();
      window.removeEventListener('online')
      window.removeEventListener('registerSocket')
      if (!user?._id) {
        socketManager.disconnect();
      }
    };
  }, [handleConnection]);

  const rejectCall = () => {
    if (incomingCall) {
      socketManager.emit('rejectCall', { toUserId: incomingCall.fromUserId });
      setIncomingCall(null);
    }
  };

  const contextValue = {
    socket: socketManager,
    isConnected,
    connectionError,
    userId: user?._id || null,
    incomingCall, setIncomingCall, rejectCall, setInCall, inCall, callInfoData, setCallInfoData
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
});
